import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, effect, inject, input, output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Message } from '../../models/message.model';
import { MessagesApiService } from '../../services/messages-api.service';
import {
  messageChangesList,
  messageFieldLabel,
  messageSnapshotList,
  messageTitleKey,
  messageTitleParams,
} from '../../utils/message-content.util';
import { SERVICE_PACKAGE_TYPE_LABELS } from '../../../service-packages/models/service-package.model';

@Component({
  selector: 'cam-message-detail-dialog',
  standalone: true,
  imports: [CommonModule, TranslatePipe, DialogModule, ButtonModule],
  templateUrl: './message-detail-dialog.component.html',
  providers: [DatePipe, CurrencyPipe],
})
export class MessageDetailDialogComponent {
  private readonly messagesApi = inject(MessagesApiService);
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);
  private readonly currencyPipe = inject(CurrencyPipe);

  visible = input.required<boolean>();
  message = input<Message | undefined>(undefined);

  visibleChange = output<boolean>();
  messageRead = output<Message>();

  constructor() {
    effect(() => {
      const visible = this.visible();
      const message = this.message();

      if (visible && message && !message.read) {
        this._markAsRead(message);
      }
    });
  }

  private _markAsRead(message: Message): void {
    this.messagesApi.markAsRead(message.id).subscribe({
      next: (updated) => this.messageRead.emit(updated),
    });
  }

  protected onClose(): void {
    this.visibleChange.emit(false);
  }

  protected getTitle(message: Message): string {
    return this.translateService.instant(
      messageTitleKey(message.type),
      messageTitleParams(message),
    );
  }

  protected isSnapshot(message: Message): boolean {
    return !!message.content.snapshot;
  }

  protected changesList(message: Message) {
    return messageChangesList(message.content);
  }

  protected snapshotList(message: Message) {
    return messageSnapshotList(message.content);
  }

  protected fieldLabel(field: string): string {
    return messageFieldLabel(field);
  }

  protected formatChangeValue(field: string, value: unknown): string {
    if (value === null || value === undefined) return '—';

    if (field === 'start' || field === 'finish') {
      return (
        this.datePipe.transform(value as string, 'dd/MM/yyyy', 'UTC') ?? '—'
      );
    }

    if (field === 'value') {
      return (
        this.currencyPipe.transform(
          value as number | string,
          'BRL',
          'symbol',
          '1.2-2',
          'pt-BR',
        ) ?? '—'
      );
    }

    if (field === 'type') {
      const labelKey = (SERVICE_PACKAGE_TYPE_LABELS as Record<string, string>)[
        value as string
      ];
      return labelKey ? this.translateService.instant(labelKey) : String(value);
    }

    return String(value);
  }
}
